import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { seekerDataService } from '../../services/seekerDataService';
import { getSupabaseClient } from '../../lib/supabase';
import { getStripe } from '../../lib/stripe';
import { type HomePageData, type PackageOffer } from '../../types';
import { AddPaymentMethodModal } from '../billing/AddPaymentMethodModal';
import { trackCapiEvent } from '../../utils/trackCapiEvent';

export type PaymentBreakdown = {
  baseTotalCents: number;
  installmentFeeCents: number;
  installmentTotalCents: number;
  depositCents: number;
  monthlyCents: number;
  installments: number;
  installmentAmounts: number[];
  firstBillingDate: Date | null;
  billingDayLabel: string;
};

type PaymentMethod = {
  id: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  isDefault: boolean;
};

type PaymentType = 'upfront' | 'installments';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const formatCurrency = (value: number) => currencyFormatter.format(value);

const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatBillingDayLabel = (date: Date) => {
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'long' });
  return `${month} ${day}${getOrdinalSuffix(day)}`;
};

const calculateFirstInstallmentDate = () => {
  const today = new Date();
  const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0, 0);

  if (normalizedToday.getDate() >= 28) {
    return new Date(normalizedToday.getFullYear(), normalizedToday.getMonth() + 2, 0, 12, 0, 0, 0);
  }

  const future = new Date(normalizedToday);
  future.setDate(normalizedToday.getDate() + 30);
  return future;
};

export const computePaymentBreakdown = (offer: PackageOffer): PaymentBreakdown => {
  const baseTotalCents = offer.totalCents;

  // 10% installment fee on the base total
  const installmentFeeCents = Math.ceil(baseTotalCents * 0.10);
  const installmentTotalCents = baseTotalCents + installmentFeeCents;

  // Deposit is 25% of the installment total (includes fee)
  const depositCents = Math.min(Math.ceil(installmentTotalCents * 0.25), installmentTotalCents);
  const remainingCents = Math.max(installmentTotalCents - depositCents, 0);

  const configuredMonths = offer.durationMonths ?? 1;
  let installments = Math.max(configuredMonths - 1, 0);

  if (remainingCents > 0 && installments === 0) {
    installments = 1;
  }

  const installmentAmounts: number[] = [];

  if (installments > 0) {
    const baseAmount = Math.floor(remainingCents / installments);
    const remainder = remainingCents - baseAmount * installments;

    for (let i = 0; i < installments; i += 1) {
      const extra = i === 0 ? remainder : 0;
      installmentAmounts.push(baseAmount + extra);
    }
  }

  const firstBillingDate = installmentAmounts.length > 0 ? calculateFirstInstallmentDate() : null;
  const billingDayLabel = firstBillingDate ? formatBillingDayLabel(firstBillingDate) : '';

  return {
    baseTotalCents,
    installmentFeeCents,
    installmentTotalCents,
    depositCents,
    monthlyCents: installmentAmounts[0] ?? 0,
    installments,
    installmentAmounts,
    firstBillingDate,
    billingDayLabel,
  };
};

const CheckoutHeader: React.FC<{ title: string; onBack?: () => void }> = ({ title, onBack }) => (
  <header className="bg-[#2C2731] text-[#FFFCEF]">
    <div className="mx-auto flex max-w-sm items-center justify-between px-4 py-4">
      <button
        type="button"
        onClick={onBack}
        disabled={!onBack}
        className="rounded-full p-2 hover:bg-[#FFFCEF]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#2C2731] disabled:cursor-default disabled:opacity-40"
      >
        <span className="sr-only">Back</span>
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 className="text-lg font-semibold">{title}</h1>
      <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
    </div>
  </header>
);

export const PackageCheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<HomePageData | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [paymentType, setPaymentType] = useState<PaymentType>('upfront');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [packageCollapsed, setPackageCollapsed] = useState(true);

  const packageOffer = data?.packageOffer ?? null;
  const guide = data?.match?.guide_details ?? null;

  const paymentBreakdown = useMemo(() => {
    if (!packageOffer) {
      return null;
    }
    return computePaymentBreakdown(packageOffer);
  }, [packageOffer]);

  const dueTodayCents = useMemo(() => {
    if (!paymentBreakdown) return 0;
    return paymentType === 'upfront' ? paymentBreakdown.baseTotalCents : paymentBreakdown.depositCents;
  }, [paymentBreakdown, paymentType]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const homePageData = await seekerDataService.getHomePageData();
        if (isMounted) {
          setData(homePageData);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load checkout data';
        if (isMounted) {
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [location.state]);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const supabase = getSupabaseClient();

      // Ensure customer exists
      await supabase.functions.invoke('stripe-ensure-customer', {
        method: 'POST',
      });

      // List payment methods
      const { data: pmData, error: listError } = await supabase.functions.invoke<{
        paymentMethods: PaymentMethod[];
      }>('stripe-list-payment-methods', {
        method: 'GET',
      });

      if (listError) {
        throw listError;
      }

      const methods = pmData?.paymentMethods || [];
      setPaymentMethods(methods);

      // Auto-select default payment method
      const defaultMethod = methods.find((m) => m.isDefault);
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod.id);
      } else if (methods.length > 0) {
        setSelectedPaymentMethod(methods[0].id);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const createSubscriptionSchedule = async (orderId: string, customerId: string) => {
    if (!paymentBreakdown || paymentBreakdown.installments === 0 || !paymentBreakdown.firstBillingDate) {
      // No recurring payments needed
      return;
    }

    try {
      const supabase = getSupabaseClient();
      const startDateUnix = Math.floor(paymentBreakdown.firstBillingDate.getTime() / 1000);

      const { error: scheduleError } = await supabase.functions.invoke('stripe-create-subscription-schedule', {
        method: 'POST',
        body: {
          order_id: orderId,
          customer_id: customerId,
          payment_method_id: selectedPaymentMethod,
          monthly_cents: paymentBreakdown.monthlyCents,
          months: paymentBreakdown.installments,
          start_date_unix: startDateUnix,
          currency: 'usd',
          installment_amounts: paymentBreakdown.installmentAmounts,
        },
      });

      if (scheduleError) {
        console.error('Failed to create subscription schedule:', scheduleError);
        // Don't throw - initial payment succeeded, we can handle schedule creation failure gracefully
      }
    } catch (err) {
      console.error('Error creating subscription schedule:', err);
      // Don't throw - initial payment succeeded
    }
  };

  const handleCompletePurchase = async () => {
    if (!packageOffer || !guide) {
      setError('Missing package or guide information');
      return;
    }

    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      setError('Please select a payment method');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      // Step 1: Create PaymentIntent (unconfirmed) and order via stripe-checkout-start
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke<{
        order_id: string;
        client_secret: string;
        payment_intent_id: string;
        customer_id?: string;
      }>('stripe-checkout-start', {
        method: 'POST',
        body: {
          order: {
            package_offer_id: packageOffer.id,
            guide_id: guide.id,
          },
          amount_cents: dueTodayCents,
          currency: 'usd',
          is_installment: paymentType === 'installments',
          base_total_cents: paymentBreakdown?.baseTotalCents ?? packageOffer.totalCents,
          installment_fee_cents: paymentType === 'installments' ? (paymentBreakdown?.installmentFeeCents ?? 0) : 0,
        },
      });

      if (checkoutError) {
        throw checkoutError;
      }

      if (!checkoutData || !checkoutData.client_secret) {
        throw new Error('No client secret received from checkout');
      }

      // Step 2: Confirm the payment client-side using Stripe.js for proper SCA/3DS handling
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe not loaded');
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        checkoutData.client_secret,
        {
          payment_method: selectedPaymentMethod || undefined,
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message || 'Payment failed');
      }

      if (!paymentIntent || paymentIntent.status !== 'succeeded') {
        throw new Error(`Payment not completed. Status: ${paymentIntent?.status ?? 'unknown'}`);
      }

      // Step 3: Call order-mark-paid to verify and mark the order as paid server-side
      const capiEventId = trackCapiEvent('Purchase');
      const { data: markPaidData, error: markPaidError } = await supabase.functions.invoke<{
        success: boolean;
        order_id: string;
        status: string;
        error?: string;
      }>('order-mark-paid', {
        method: 'POST',
        body: {
          order_id: checkoutData.order_id,
          payment_intent_id: checkoutData.payment_intent_id,
          capi_event_id: capiEventId,
        },
      });

      if (markPaidError) {
        console.error('Failed to mark order as paid:', markPaidError);
        // Payment succeeded but order-mark-paid failed - still navigate to success
        // The webhook or manual intervention can handle this edge case
      }

      if (markPaidData && !markPaidData.success && markPaidData.error) {
        console.error('order-mark-paid returned error:', markPaidData.error);
      }

      // Step 4: Create subscription schedule for remaining payments (only if installments)
      if (paymentType === 'installments' && checkoutData.customer_id) {
        await createSubscriptionSchedule(checkoutData.order_id, checkoutData.customer_id);
      }

      // Navigate to success page
      navigate('/checkout/success', { state: { orderId: checkoutData.order_id } });
    } catch (err) {
      console.error('Purchase failed:', err);
      setError(err instanceof Error ? err.message : 'Purchase failed. Please try again.');
      setProcessing(false);
    }
  };

  const handleSaveForLater = () => {
    navigate('/home');
  };

  const handleAddPaymentMethod = () => {
    setShowAddPaymentModal(true);
  };

  const handlePaymentMethodAdded = async () => {
    setShowAddPaymentModal(false);
    // Reload payment methods and select the newest one
    try {
      setLoadingPaymentMethods(true);
      const supabase = getSupabaseClient();

      const { data: pmData, error: listError } = await supabase.functions.invoke<{
        paymentMethods: PaymentMethod[];
      }>('stripe-list-payment-methods', {
        method: 'GET',
      });

      if (listError) {
        throw listError;
      }

      const methods = pmData?.paymentMethods || [];
      setPaymentMethods(methods);

      // Select the most recently added payment method (first in the list)
      if (methods.length > 0) {
        setSelectedPaymentMethod(methods[0].id);
      }
    } catch (err) {
      console.error('Failed to reload payment methods:', err);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const groupedUnits = useMemo(() => {
    if (!packageOffer) return [];

    const groups = new Map<string, {
      componentType: string;
      componentName: string;
      unitPriceCents: number;
      count: number;
    }>();

    packageOffer.units.forEach((unit) => {
      const key = `${unit.componentType}-${unit.componentName}-${unit.unitPriceCents}`;
      const existing = groups.get(key);

      if (existing) {
        existing.count += 1;
      } else {
        groups.set(key, {
          componentType: unit.componentType,
          componentName: unit.componentName,
          unitPriceCents: unit.unitPriceCents,
          count: 1,
        });
      }
    });

    return Array.from(groups.values());
  }, [packageOffer]);

  const renderGroupedUnit = (group: { componentType: string; componentName: string; unitPriceCents: number; count: number }, index: number) => {
    return (
      <div
        key={`${group.componentType}-${group.componentName}-${index}`}
        className="rounded-xl border border-[#2C2731]/60 bg-[#FFFCEF] px-3 py-3 shadow-sm"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-[#2C2731]">{group.componentName}</p>
            <p className="mt-1 text-xs capitalize text-[#2C2731]/70">{group.componentType}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-[#2C2731]">
              {formatCurrency(group.unitPriceCents / 100)}
            </p>
            <p className="text-xs text-[#2C2731]/70">
              {group.count} session{group.count !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const getCardBrandIcon = (brand?: string) => {
    return '💳';
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-[#FFFCEF]">
        <CheckoutHeader title="Checkout" onBack={() => navigate(-1)} />
        <main className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#2C2731]" />
            <p className="text-sm text-[#2C2731]/70">Loading checkout details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error && !packageOffer) {
    return (
      <div className="flex min-h-screen flex-col bg-[#FFFCEF]">
        <CheckoutHeader title="Checkout" onBack={() => navigate(-1)} />
        <main className="flex flex-1 items-center justify-center px-4">
          <div className="w-full max-w-sm rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 text-center shadow-sm">
            <p className="mb-4 text-sm font-semibold text-red-600">Error: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-lg bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#1F1924]"
            >
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!packageOffer) {
    return (
      <div className="flex min-h-screen flex-col bg-[#FFFCEF]">
        <CheckoutHeader title="Checkout" onBack={() => navigate(-1)} />
        <main className="mx-auto flex w-full max-w-sm flex-1 items-center justify-center px-4">
          <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 text-center shadow-sm">
            <h2 className="text-lg font-semibold text-[#2C2731]">No package offer available</h2>
            <p className="mt-2 text-sm text-[#2C2731]/70">
              Return to your dashboard to review available offers.
            </p>
            <button
              onClick={() => navigate('/home')}
              className="mt-4 w-full rounded-lg bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#1F1924]"
            >
              Back to Home
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
      <div className="flex h-screen flex-col bg-[#FFFCEF] overflow-y-auto">
      <CheckoutHeader title="Checkout" onBack={() => navigate(-1)} />

      <main className="mx-auto w-full max-w-sm flex-1 space-y-6 px-4 py-6">
        {error && (
          <div className="rounded-2xl border border-red-300 bg-[#FBE2E2] px-4 py-3 text-sm text-red-700 shadow-sm">
            {error}
          </div>
        )}

        <section>
          <button
            type="button"
            onClick={() => setPackageCollapsed(!packageCollapsed)}
            className="w-full rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-4 shadow-sm text-left transition hover:bg-[#FFFCEF]/80"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#2C2731]/20 bg-[#E8DFBD]">
                  {guide?.image ? (
                    <img src={guide.image} alt={guide.name} className="h-full w-full object-cover" />
                  ) : (
                    <svg className="h-5 w-5 text-[#2C2731]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#2C2731] truncate">{packageOffer.name}</p>
                  <p className="text-xs text-[#2C2731]/60 ph-mask">
                    {guide?.name ?? 'Your Guide'} &middot; {formatCurrency(packageOffer.totalCents / 100)}
                  </p>
                </div>
              </div>
              <svg
                className={`h-5 w-5 shrink-0 text-[#2C2731]/50 transition-transform ${packageCollapsed ? '' : 'rotate-180'}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {packageCollapsed && (
              <p className="mt-2 text-xs text-[#2C2731]/50">Tap to view package details</p>
            )}
          </button>

          {!packageCollapsed && (
            <div className="mt-3 space-y-4 rounded-2xl border border-[#2C2731]/40 bg-[#FFFCEF] p-4">
              {/* Guide + package info */}
              <div className="flex items-start space-x-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#2C2731]/20 bg-[#E8DFBD]">
                  {guide?.image ? (
                    <img src={guide.image} alt={guide.name} className="h-full w-full object-cover" />
                  ) : (
                    <svg className="h-7 w-7 text-[#2C2731]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-base font-semibold text-[#2C2731] ph-mask">{guide?.name ?? 'Your Guide'}</h2>
                  {guide?.description && (
                    <p className="text-sm text-[#2C2731]/70 line-clamp-2">{guide.description}</p>
                  )}
                  <div className="mt-2 flex items-center space-x-2 text-xs text-[#2C2731]/60">
                    <span>{guide?.focus_areas?.[0] ?? 'Premium guidance'}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-[#2C2731]/40 bg-[#E8DFBD] p-3">
                <h3 className="text-sm font-semibold text-[#2C2731]">{packageOffer.name}</h3>
                <p className="mt-1 text-sm text-[#2C2731]/80 line-clamp-3">
                  {packageOffer.description || 'Your curated program tailored to your therapeutic goals.'}
                </p>
                <p className="mt-2 text-xs text-[#2C2731]/60">
                  Duration: {packageOffer.durationMonths ? `${packageOffer.durationMonths} months` : 'Custom'}
                </p>
              </div>

              {/* Package details (units) — moved from bottom */}
              {groupedUnits.length > 0 && (
                <div className="space-y-2 border-t border-[#2C2731]/20 pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/50">
                    Included Sessions
                  </p>
                  {groupedUnits.map(renderGroupedUnit)}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/70">
            Payment Method
          </p>
          {loadingPaymentMethods ? (
            <div className="flex justify-center py-4">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#2C2731]" />
            </div>
          ) : (
            <div className="space-y-2">
              {paymentMethods.map((method) => {
                const isSelected = selectedPaymentMethod === method.id;
                return (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedPaymentMethod(method.id)}
                    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
                      isSelected
                        ? 'border-[#2C2731] bg-[#E8DFBD]'
                        : 'border-[#2C2731]/40 bg-[#FFFCEF] hover:bg-[#E8DFBD]'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getCardBrandIcon(method.brand)}</span>
                      <div className="text-sm text-[#2C2731]">
                        <p className="font-semibold">
                          {method.brand?.charAt(0).toUpperCase()}
                          {method.brand?.slice(1)} •••• {method.last4}
                        </p>
                        <p className="text-xs text-[#2C2731]/70">
                          Expires {method.expMonth?.toString().padStart(2, '0')}/{method.expYear?.toString().slice(-2)}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={handleAddPaymentMethod}
                className="flex w-full items-center justify-between rounded-xl border border-[#2C2731]/40 bg-[#FFFCEF] px-4 py-3 text-left transition hover:bg-[#E8DFBD]"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#2C2731]/40 bg-[#FFFCEF]">
                    <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C2731]">Add New Payment Method</p>
                    <p className="text-xs text-[#2C2731]/70">Securely store a new card</p>
                  </div>
                </div>
                <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/70">
            Payment Options
          </p>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setPaymentType('installments')}
              className={`flex w-full items-start justify-between rounded-xl border px-4 py-3 text-left transition ${
                paymentType === 'installments'
                  ? 'border-[#2C2731] bg-[#E8DFBD]'
                  : 'border-[#2C2731]/40 bg-[#FFFCEF] hover:bg-[#E8DFBD]'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <p className="font-semibold text-[#2C2731]">Pay in Installments</p>
                  <span className="rounded-full bg-[#2C2731]/10 px-2 py-0.5 text-xs font-medium text-[#2C2731]/70">
                    +10% fee
                  </span>
                </div>
                <p className="mt-1 text-sm text-[#2C2731]/70">
                  25% deposit today, remaining balance in {paymentBreakdown?.installments || 1} monthly payment{(paymentBreakdown?.installments || 1) !== 1 ? 's' : ''}
                </p>
                {paymentBreakdown && (
                  <p className="mt-2 text-sm font-semibold text-[#2C2731]">
                    {formatCurrency(paymentBreakdown.depositCents / 100)} today ({formatCurrency(paymentBreakdown.installmentTotalCents / 100)} total)
                  </p>
                )}
              </div>
              {paymentType === 'installments' && (
                <svg className="h-5 w-5 flex-shrink-0 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>

            <button
              type="button"
              onClick={() => setPaymentType('upfront')}
              className={`flex w-full items-start justify-between rounded-xl border px-4 py-3 text-left transition ${
                paymentType === 'upfront'
                  ? 'border-[#2C2731] bg-[#E8DFBD]'
                  : 'border-[#2C2731]/40 bg-[#FFFCEF] hover:bg-[#E8DFBD]'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <p className="font-semibold text-[#2C2731]">Pay in Full</p>
                  <span className="rounded-full bg-[#2C2731] px-2 py-0.5 text-xs font-semibold text-[#FFFCEF]">
                    Recommended
                  </span>
                </div>
                <p className="mt-1 text-sm text-[#2C2731]/70">
                  Pay the entire amount today, no recurring payments
                </p>
                {paymentBreakdown && (
                  <p className="mt-2 text-sm font-semibold text-[#2C2731]">
                    {formatCurrency(paymentBreakdown.baseTotalCents / 100)} today
                  </p>
                )}
              </div>
              {paymentType === 'upfront' && (
                <svg className="h-5 w-5 flex-shrink-0 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          </div>
        </section>

        {paymentBreakdown && (
          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]/70">
              Pricing &amp; Billing Details
            </p>
            <div className="space-y-3 rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-4 shadow-sm">
              <div className="flex justify-between text-sm text-[#2C2731]">
                <span>Package Price</span>
                <span className="font-semibold">{formatCurrency(paymentBreakdown.baseTotalCents / 100)}</span>
              </div>
              {paymentType === 'installments' && (
                <>
                  <div className="flex justify-between text-sm text-[#2C2731]">
                    <span>Installment Fee (10%)</span>
                    <span className="font-semibold">{formatCurrency(paymentBreakdown.installmentFeeCents / 100)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-[#2C2731] border-t border-[#2C2731]/20 pt-2">
                    <span>Total with Installments</span>
                    <span className="font-semibold">{formatCurrency(paymentBreakdown.installmentTotalCents / 100)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-[#2C2731]">
                    <span>25% Deposit (Today)</span>
                    <span className="font-semibold">{formatCurrency(paymentBreakdown.depositCents / 100)}</span>
                  </div>
                  {paymentBreakdown.installments > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-[#2C2731]">
                        <span>Monthly Installments ({paymentBreakdown.installments})</span>
                        <span className="font-semibold">
                          {formatCurrency(paymentBreakdown.monthlyCents / 100)}
                        </span>
                      </div>
                      <p className="text-xs text-[#2C2731]/70">
                        Next payment on {paymentBreakdown.billingDayLabel}.{' '}
                        {paymentBreakdown.installments > 1
                          ? `The remaining ${paymentBreakdown.installments - 1} payment${paymentBreakdown.installments - 1 === 1 ? '' : 's'} bill every ~30 days thereafter.`
                          : 'This will be your final installment.'}
                      </p>
                    </>
                  )}
                </>
              )}
              {paymentType === 'upfront' && (
                <p className="text-xs text-[#2C2731]/70">
                  One-time payment. No recurring charges will be applied.
                </p>
              )}
              <div className="border-t border-[#2C2731]/30 pt-3">
                <div className="flex justify-between text-base font-semibold text-[#2C2731]">
                  <span>Total Due Today</span>
                  <span>{formatCurrency(dueTodayCents / 100)}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="space-y-2 pb-6">
          <button
            type="button"
            onClick={handleCompletePurchase}
            disabled={processing || loadingPaymentMethods}
            className="w-full rounded-lg bg-[#2C2731] py-3 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#1F1924] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {processing ? 'Processing...' : `Complete Purchase - ${formatCurrency(dueTodayCents / 100)}`}
          </button>
        </section>
      </main>

      <AddPaymentMethodModal
        isOpen={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        onAdded={handlePaymentMethodAdded}
      />
    </div>
  );
};

export default PackageCheckoutPage;
